import { h } from 'preact'
import { StateUpdater } from 'preact/hooks'
import { TFunctionDetailedResult } from 'i18next'
import classNames from 'classnames'

import { CurrencyValueForm } from 'models/apps'

import { formatCurrencyAmount } from 'apps-utils/validate-currency-input'
import { CalculatorCurrencyInputWithSelect } from 'common-modules/calculator-currency-input-with-select'

import { AmountName } from '../hooks/useTransferCalculation'
import { getAmountErrorMessage, isCurrencyNotSupported, shouldShowAmountError } from '../utils/form-errors'

interface CalculatorInputProps {
  id: string
  label: TFunctionDetailedResult
  isBaseCurrencyComponent: boolean
  debounceTime: number
  amount: string
  baseCurrency: string
  counterCurrency: string
  apiErrorMessage: string
  currencies: string[]
  receivedAmount: string
  isCalculationLoading: boolean
  handleAmountChange: (value: string, amountName: AmountName) => void
  handleSetBaseCurrency: (currency: string) => void
  handleSetCounterCurrency: (currency: string) => void
}

const CalculatorInput = ({
  id,
  label,
  isBaseCurrencyComponent,
  amount,
  baseCurrency,
  counterCurrency,
  apiErrorMessage,
  currencies,
  receivedAmount,
  isCalculationLoading,
  handleAmountChange,
  debounceTime,
  handleSetBaseCurrency,
  handleSetCounterCurrency,
}: CalculatorInputProps) => {
  const amountName: AmountName = isBaseCurrencyComponent ? 'baseAmount' : 'counterAmount'
  const errorMessage = getAmountErrorMessage(
    apiErrorMessage,
    amount,
    receivedAmount,
    isBaseCurrencyComponent,
    isCalculationLoading,
  )

  const handleValueChange: StateUpdater<CurrencyValueForm> = (value) => {
    const resolvedValue = typeof value === 'function' ? value(formatCurrencyAmount(amount)) : value
    handleAmountChange(resolvedValue.currentValue, amountName)
  }

  return (
    <div
      className={classNames('transfer-calculator__field', {
        'transfer-calculator__field--error': isCurrencyNotSupported(apiErrorMessage),
      })}
    >
      <p className="transfer-calculator__label">
        <b>{label}</b>
      </p>
      <CalculatorCurrencyInputWithSelect
        id={id}
        value={formatCurrencyAmount(amount)}
        setValue={handleValueChange}
        isBelowMinimalAmount={shouldShowAmountError(
          apiErrorMessage,
          amount,
          receivedAmount,
          isBaseCurrencyComponent,
          isCalculationLoading,
        )}
        debounceTime={debounceTime}
        isBaseCurrencyComponent={isBaseCurrencyComponent}
        baseCurrency={baseCurrency}
        counterCurrency={counterCurrency}
        handleSetBaseCurrency={handleSetBaseCurrency}
        handleSetCounterCurrency={handleSetCounterCurrency}
        fixedCurrencyList={currencies}
        disableSwapOnCollision={true}
      />
      {errorMessage && <p className="transfer-calculator__error-text">{errorMessage}</p>}
    </div>
  )
}

export default CalculatorInput
